import React, { useEffect, useState } from 'react';
import { Col, FormGroup, Label, Row } from 'reactstrap';
import Cleave from 'cleave.js/react';
import { DropDownAutoComplete } from '../../../../components/inputs/DropDownAutoComplete';
import styles from './Payment.module.css';
import useTranslation from '../../../contexts/LanguageTranslation/useTranslation';
import StandardInput from '../../../shared/StandardInput/StandardInput';
import { DropDownInput } from '../../../inputs/DropDownInput';
import AppleIntlReplacementModal from '../../../modals/AppleInternationalReplacementModal';
import { formFieldLabels } from './addressFieldLabels';
import { countryNameFromCode } from '../../../../helpers';

const PostalCode = ({ zipValidator, countryLabels, zip, handleInputChange, block }) => {
    return (
        <FormGroup className={zipValidator() && 'form-error'}>
            <Label
                for='zipCode'
                className='form-label'
            >
                {countryLabels.zip}
            </Label>
            <span
                aria-hidden='true'
                className='required-indicator'
            >
                *
            </span>
            <Cleave
                options={{
                    blocks: block,
                    numericOnly: false,
                    delimiters: [' ']
                }}
                className='form-control'
                id='zipCode'
                name='zip'
                aria-required='true'
                onChange={(event) => handleInputChange(event)}
                value={zip}
            />
            {zipValidator()}
        </FormGroup>
    );
};

export const AddressForm = ({
    includeNameFields = false,
    address,
    states,
    cities,
    handleInputChange,
    handleStateDropDown,
    handleCityDropDown,
    validator,
    validationFired,
    allowVariableCountry = false,
    onUpdateStates = () => {},
    onUpdateCities = () => {},
    clientId,
    countryCode,
    isChileApple = false,
    disableCountryDropDown = false,
    showCountryChangeModal = true
}) => {
    let { firstName, lastName, address1, address2, city, state, stateId, zip } = address;

    const international = useTranslation('components/Vendor/Apple/IntakeLoss/CountryAddressForms');
    const { country, language, translations } = international;
    const [variableCountry, setVariableCountry] = useState(
        address?.country?.toUpperCase() || address?.countryCode?.toUpperCase() || country
    );
    const [variableStateId, setVariableStateId] = useState(address?.stateId || '');
    const [cityId, setCityId] = useState('');
    const [tempNextCountry, setTempNextCountry] = useState(null);
    const [isChangeCountryModalOpen, setIsChangeCountryModalOpen] = useState(false);
    const [countryLabels, setCountryLabels] = useState({ ...formFieldLabels[variableCountry]?.['en'] });
    const [statesData, setStatesData] = useState(states);
    const [activeCountries, setActiveCountries] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);

    useEffect(() => {
        setTempNextCountry(null);
        if (address?.country?.toLowerCase() !== variableCountry?.toLowerCase()) {
            fetchStates();
        }
        validator.purgeFields();
        let labelSet = formFieldLabels[variableCountry]?.[language];

        if (!labelSet) {
            labelSet = formFieldLabels[variableCountry]?.['en'];
        }

        setCountryLabels({
            ...countryLabels,
            ...labelSet
        });
    }, [variableCountry, language]);

    useEffect(() => {
        if (variableCountry === 'CL') {
            fetchCities();
        }
    }, [address?.stateId]);

    const fetchStates = async () => {
        await fetch('api/party/states/' + variableCountry + '/' + clientId)
            .then((res) => res.json())
            .then((data) => {
                let results = data.map((s) => {
                    return { data: s.stateId, display: s.name, abbreviation: s.abbreviation };
                });
                onUpdateStates(results, variableCountry);
            });
    };

    const fetchCities = async () => {
        if (address?.stateId !== null) {
            await fetch('api/party/localitylist/' + address?.stateId + '/' + clientId)
                .then((res) => res.json())
                .then((data) => {
                    const results = data?.map((city) => {
                        return { data: city.localityId, display: city.name };
                    });
                    const currentCityId = results?.filter((city) => city.display === address?.city)[0]?.data || null;
                    currentCityId && setCityId(currentCityId);
                    onUpdateCities(results, address?.stateId);
                });
        }
    };

    useEffect(() => {
        if (language === 'ja' && variableCountry == 'JP') {
            const options = states
                ?.filter((state) => state?.display !== 'Japan')
                .map((data) => ({
                    ...data,
                    display: (translations.states && translations.states[data?.display?.toLowerCase()]) || data?.display
                }));
            setStatesData(options);
        } else {
            setStatesData(states);
        }
    }, [translations.states, language, states]);

    const handleChangeCountries = (sel) => {
        if (sel === variableCountry) return;

        setTempNextCountry(sel);
        if (showCountryChangeModal) {
            setIsChangeCountryModalOpen(true);
        } else {
            setVariableCountry(sel);
        }
    };

    const handleChangeStates = (sel) => {
        if (sel === variableStateId) return;

        setVariableStateId(sel);
        handleStateDropDown(sel);
    };

    const handleChangeCities = (sel) => {
        if (sel === cityId) return;

        setCityId(sel);
        handleCityDropDown(sel);
    };

    const handleChangeCountryConfirm = (nextCountry) => {
        setVariableCountry(nextCountry);
        setIsChangeCountryModalOpen(false);
    };

    useEffect(() => {
        fetch('api/party/activecountries/' + clientId)
            .then((res) => res.json())
            .then((data) => {
                setActiveCountries(data);
            });
    }, []);

    const updateCountryOptions = (data) => {
        const countries = {
            unitedkingdom: 'uk',
            unitedstates: 'usa'
        };

        const availableCountries = data
            .map((opt) => {
                const countryNameInEnglish = opt.name;
                const formattedCountryName = countryNameInEnglish?.replace(/\s/g, '')?.toLowerCase();
                const translationKey = countries[formattedCountryName] || formattedCountryName;

                return {
                    data: opt.iso2 === 'AU' ? 'AUS' : opt.iso2 === 'GB' ? 'UK' : opt.iso2,
                    display: translations.countryOptions?.[translationKey] || countryNameInEnglish
                };
            })
            .sort((a, b) => (a.display > b.display ? 1 : -1));

        setCountryOptions(availableCountries);
    };

    useEffect(() => {
        if (activeCountries?.length > 0 && (translations.countryOptions || language === 'en')) {
            updateCountryOptions(activeCountries);
        }
    }, [activeCountries, translations.countryOptions]);

    const standardGlobalCountries = ['AT', 'DK', 'FI', 'FR', 'DE', 'NL', 'NO', 'SE', 'CH'];

    let zipValidator = () => {
        switch (variableCountry) {
            case 'UK':
                return validator.message('postal code', zip, 'required|min:5|max:8|UKZipRegex', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Postal code is required.',
                        UKZipRegex: translations?.errors?.postalInvalid || 'Please enter a valid postal code.',
                        min: translations?.errors?.postalInvalid || 'Please enter a valid postal code.'
                    }
                });
            case 'AUS':
                return validator.message('postal code', zip, 'required|numeric|size:4', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Postal code is required.',
                        numeric: translations?.errors?.postalInvalid || 'Please enter a valid postal code.',
                        size: translations?.errors?.postalInvalid || 'Please enter a valid postal code.'
                    }
                });
            case 'CL':
                return validator.message('postal code', zip, zip?.length > 0 ? 'numeric|min:4' : '', {
                    messages: {
                        numeric: translations?.errors?.postalInvalid || 'Please enter a valid postal code.',
                        min: translations?.errors?.postalInvalid || 'Please enter a valid postal code.'
                    }
                });
            case 'AT':
            case 'DK':
            case 'FI':
            case 'FR':
            case 'DE':
            case 'NO':
            case 'CH':
                return validator.message('postal code', zip, 'required|alpha_num_space|min:4|max:10', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Postal code is required.',
                        alpha_num_space: translations?.errors?.postalInvalid || 'Please enter a valid postal code.',
                        min: translations?.errors?.postalInvalid || 'Please enter a valid postal code.'
                    }
                });
            case 'SE':
                return validator.message('postal code', zip, 'required|size:6|regex:^[0-9]{3} [0-9]{2}', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Postal code is required.',
                        regex: translations?.errors?.postalInvalid || 'Please enter a valid postal code.',
                        size: translations?.errors?.postalInvalid || 'Please enter a valid postal code.'
                    }
                });
            case 'NL':
                return validator.message('postal code', zip, 'required|size:7|regex:^[0-9]{4} [a-zA-Z]{2}', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Postal code is required.',
                        regex: translations?.errors?.postalInvalid || 'Please enter a valid postal code.',
                        size: translations?.errors?.postalInvalid || 'Please enter a valid postal code.'
                    }
                });
            case 'IE':
                return validator.message('eircode', zip, 'required|alpha_num_space|min:4|max:10', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Eircode is required.',
                        alpha_num_space: translations?.errors?.postalInvalid || 'Please enter a valid eircode.',
                        min: translations?.errors?.postalInvalid || 'Please enter a valid eircode.'
                    }
                });
            case 'NZ':
                return validator.message('postcode', zip, 'required|alpha_num|min:4|max:10', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Postcode is required.',
                        alpha_num: translations?.errors?.postalInvalid || 'Please enter a valid postcode.',
                        min: translations?.errors?.postalInvalid || 'Please enter a valid postcode.'
                    }
                });
            case 'JP':
                return validator.message('postal code', zip, 'required|size:8', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'Postal code is required.',
                        size: translations?.errors?.postalInvalid || 'Please enter a valid postal code.'
                    }
                });
            case 'IN':
                return validator.message('pincode', zip, 'required|size:6', {
                    messages: {
                        required: translations?.errors?.postalRequired || 'PIN code is required.',
                        size: translations?.errors?.postalInvalid || 'Please enter a valid PIN code.'
                    }
                });
            case 'IT':
            case 'ES':
            case 'US':
            default:
                return validator.message('postal code', zip, 'required|numeric|size:5', {
                    messages: {
                        required:
                            translations?.errors?.postalRequired
                            || `${variableCountry === 'US' ? 'ZIP' : 'Postal'} code is required.`,
                        numeric:
                            translations?.errors?.postalInvalid
                            || `Please enter a valid ${variableCountry === 'US' ? 'zip' : 'postal'} code.`,
                        size:
                            translations?.errors?.postalInvalid
                            || `Please enter a valid ${variableCountry === 'US' ? 'zip' : 'postal'} code.`
                    }
                });
        }
    };

    const getZipBlockFormat = (country) => {
        switch (country) {
            case 'NL':
                return [4, 2];
            case 'SE':
                return [3, 2];
            default:
                return [10];
        }
    };

    // block component rendering until translation service call resolves translations
    if (!international.isTranslationResolved) return null;

    const address2Input = (countryCode) => {
        if (countryCode === 'VN' || standardGlobalCountries.includes(countryCode) || countryCode === 'NZ') {
            return null;
        }
        return (
            <StandardInput
                notRequired
                label={countryLabels.address2}
                fieldName='address2'
                ariaLabelAndFieldId={'address2'}
                value={address2}
                onChange={handleInputChange}
                maxLength={50}
            />
        );
    };

    const address1RequiredMsg = () => {
        if (translations?.errors?.address1Required) {
            return translations?.errors?.address1Required;
        } else {
            if (variableCountry === 'IT' || variableCountry === 'ES' || variableCountry === 'IN') {
                return 'Address is required.';
            }
            if (variableCountry === 'MX') {
                return 'Street and number is required.';
            }
            return 'Street address is required.';
        }
    };

    const poBoxRequired = () => {
        if (validator.check(address1, `required${variableCountry === 'US' ? '|pobox_check' : ''}`)) {
            return false;
        } else {
            return true;
        }
    };

    const addressInputFields = () => {
        return (
            <>
                <StandardInput
                    label={countryLabels.address1}
                    hasError={poBoxRequired() && validationFired}
                    fieldName='address1'
                    ariaLabelAndFieldId={'address1'}
                    value={address1}
                    maxLength={150}
                    onChange={handleInputChange}
                    validator={validator.message(
                        'address',
                        address1,
                        `required${variableCountry === 'US' ? '|pobox_check' : ''}`,
                        {
                            messages: {
                                required: address1RequiredMsg()
                            }
                        }
                    )}
                />
                {address2Input(variableCountry)}
            </>
        );
    };

    return (
        <div id='addressFields'>
            {includeNameFields && (
                <Row className={'multiColWrapper'}>
                    <Col sm='6'>
                        <StandardInput
                            label={translations.fName || 'First Name'}
                            hasError={!firstName && validationFired}
                            fieldName='firstName'
                            ariaLabelAndFieldId={'firstName'}
                            value={firstName}
                            maxLength={100}
                            onChange={handleInputChange}
                            validator={validator.message('firstName', firstName, 'required', {
                                messages: {
                                    required: translations.errors?.fNameRequired || 'First name is required.'
                                }
                            })}
                        />
                    </Col>
                    <Col sm='6'>
                        <StandardInput
                            label={translations.lName || 'Last Name'}
                            hasError={!lastName && validationFired}
                            fieldName='lastName'
                            ariaLabelAndFieldId={'lastName'}
                            key='lastName'
                            value={lastName}
                            maxLength={100}
                            onChange={handleInputChange}
                            validator={validator.message('lastName', lastName, 'required', {
                                messages: {
                                    required: translations.errors?.lNameRequired || 'Last name is required.'
                                }
                            })}
                        />
                    </Col>
                </Row>
            )}

            {allowVariableCountry && (
                <FormGroup className={!variableCountry && validationFired && 'form-error'}>
                    <Label
                        for='shipToCountry'
                        className='form-label'
                    >
                        {isChileApple
                            ? translations.country || 'Country'
                            : translations.countryRegion || 'Country/Region'}
                    </Label>
                    <span
                        aria-hidden='true'
                        className='required-indicator'
                    >
                        *
                    </span>
                    <DropDownInput
                        id='shipToCountry'
                        options={countryOptions}
                        defaultValue={variableCountry}
                        onChangedValue={handleChangeCountries}
                        disabled={disableCountryDropDown}
                    />
                </FormGroup>
            )}

            {variableCountry === 'JP' && (
                <>
                    <FormGroup className={zipValidator() && validationFired && 'form-error'}>
                        <Label
                            for='zipCode'
                            className='form-label'
                        >
                            {countryLabels.zip}
                        </Label>
                        <span
                            aria-hidden='true'
                            className='required-indicator'
                        >
                            *
                        </span>
                        <Cleave
                            options={{
                                blocks: [3, 4],
                                numericOnly: true,
                                delimiter: '-'
                            }}
                            className='form-control'
                            id='zipCode'
                            name='zip'
                            onChange={(event) => handleInputChange(event)}
                            value={zip}
                        />
                        {zipValidator()}
                    </FormGroup>
                    <FormGroup className={!stateId && validationFired && 'form-error'}>
                        <Label
                            htmlFor='state'
                            aria-label='state'
                            className='form-label'
                        >
                            {countryLabels.state}
                        </Label>
                        <span
                            aria-hidden='true'
                            className='required-indicator'
                        >
                            *
                        </span>
                        <DropDownAutoComplete
                            id='state'
                            name='state'
                            options={statesData}
                            className='form-control'
                            aria-required='true'
                            onChangedValue={handleStateDropDown}
                            isFloatingDropdown={false}
                            onBlur={handleStateDropDown}
                            defaultValue={stateId === '' ? undefined : stateId}
                            Value={stateId}
                            placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                        />
                        {validator.message('state', stateId, 'required', {
                            messages: {
                                required: translations?.errors?.stateRequired || `${countryLabels.state} is required.`
                            }
                        })}
                    </FormGroup>
                    <StandardInput
                        label={countryLabels.city}
                        hasError={!city && validationFired}
                        fieldName='city'
                        ariaLabelAndFieldId={'city'}
                        value={city}
                        maxLength={50}
                        onChange={handleInputChange}
                        validator={validator.message('city', city, 'required', {
                            messages: {
                                required: translations?.errors?.cityRequired || `${countryLabels.city} is required.`
                            }
                        })}
                    />
                </>
            )}

            {addressInputFields()}

            {(variableCountry === 'IT' || variableCountry === 'ES' || variableCountry === 'MX') && (
                <FormGroup className={!zip && validationFired && 'form-error'}>
                    <Label
                        for='zipCode'
                        className='form-label'
                    >
                        {countryLabels.zip}
                    </Label>
                    <span
                        aria-hidden='true'
                        className='required-indicator'
                    >
                        *
                    </span>
                    <Cleave
                        options={{
                            blocks: [5],
                            numericOnly: true
                        }}
                        className='form-control'
                        id='zipCode'
                        name='zip'
                        aria-required='true'
                        onChange={handleInputChange}
                        value={zip}
                    />
                    {zipValidator()}
                </FormGroup>
            )}

            {standardGlobalCountries.includes(variableCountry) && (
                <>
                    <StandardInput
                        notRequired
                        label={countryLabels.address2}
                        fieldName='address2'
                        ariaLabelAndFieldId={'address2'}
                        value={address2}
                        onChange={handleInputChange}
                        maxLength={50}
                    />
                    <PostalCode
                        zipValidator={zipValidator}
                        countryLabels={countryLabels}
                        zip={zip}
                        handleInputChange={handleInputChange}
                        block={getZipBlockFormat(variableCountry)}
                    />
                    <StandardInput
                        label={countryLabels.city}
                        hasError={!city && validationFired}
                        fieldName='city'
                        ariaLabelAndFieldId={'city'}
                        value={city}
                        maxLength={50}
                        onChange={handleInputChange}
                        validator={validator.message('city', city, 'required', {
                            messages: {
                                required: translations?.errors?.cityRequired || `${countryLabels.city} is required.`
                            }
                        })}
                    />
                </>
            )}

            {variableCountry === 'VN' && (
                <Row className='multiColWrapper'>
                    <Col sm='6'>
                        <StandardInput
                            hasError={!address2 && validationFired}
                            label={countryLabels.address2}
                            fieldName='address2'
                            ariaLabelAndFieldId={'address2'}
                            value={address2}
                            maxLength={50}
                            onChange={handleInputChange}
                            validator={validator.message('address2', address2, 'required', {
                                messages: {
                                    required:
                                        translations.errors?.address2Required
                                        || `${countryLabels.address2} is required.`
                                }
                            })}
                        />
                    </Col>
                    <Col sm='6'>
                        <StandardInput
                            label={countryLabels.city}
                            hasError={!city && validationFired}
                            fieldName='city'
                            ariaLabelAndFieldId={'city'}
                            value={city}
                            maxLength={50}
                            onChange={handleInputChange}
                            validator={validator.message('city', city, 'required', {
                                messages: {
                                    required: translations?.errors?.cityRequired || `${countryLabels.city} is required.`
                                }
                            })}
                        />
                    </Col>
                </Row>
            )}

            {variableCountry === 'CL' && (
                <FormGroup className={zipValidator() && validationFired && 'form-error'}>
                    <Label
                        for='zipCode'
                        className='form-label'
                    >
                        {countryLabels.zip}
                    </Label>
                    <Cleave
                        options={{
                            blocks: [7],
                            numericOnly: true
                        }}
                        className='form-control'
                        id='zipCode'
                        name='zip'
                        onChange={(event) => handleInputChange(event)}
                        value={zip}
                    />
                    {zipValidator()}
                </FormGroup>
            )}

            {variableCountry === 'NZ' && (
                <StandardInput
                    hasError={!address2 && validationFired}
                    label={countryLabels.address2}
                    fieldName='address2'
                    ariaLabelAndFieldId={'address2'}
                    value={address2}
                    maxLength={50}
                    onChange={handleInputChange}
                    validator={validator.message('address2', address2, 'required', {
                        messages: {
                            required: translations.errors?.address2Required || `${countryLabels.address2} is required.`
                        }
                    })}
                />
            )}

            {(variableCountry === 'AUS'
                || variableCountry === 'UK'
                || variableCountry === 'IT'
                || variableCountry === 'ES'
                || variableCountry === 'IE'
                || variableCountry === 'NZ'
                || variableCountry === 'IN') && (
                <StandardInput
                    label={countryLabels.city}
                    hasError={!city && validationFired}
                    fieldName='city'
                    ariaLabelAndFieldId={'city'}
                    value={city}
                    maxLength={50}
                    onChange={handleInputChange}
                    validator={validator.message('city', city, 'required', {
                        messages: {
                            required: translations?.errors?.cityRequired || `${countryLabels.city} is required.`
                        }
                    })}
                />
            )}

            {variableCountry === 'CL' && (
                <>
                    <FormGroup className={!stateId && validationFired && 'form-error'}>
                        <Label
                            htmlfor='state'
                            aria-label='state'
                            className='form-label'
                        >
                            {countryLabels.state}
                        </Label>
                        <span
                            aria-hidden='true'
                            className='required-indicator'
                        >
                            *
                        </span>
                        <DropDownAutoComplete
                            name='state'
                            id='state'
                            options={states}
                            className='form-control'
                            onChangedValue={handleChangeStates}
                            placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                            isFloatingDropdown={false}
                            onBlur={handleChangeStates}
                            defaultValue={stateId === '' ? undefined : stateId}
                            Value={stateId}
                        />
                        {validator.message('state', stateId, 'required', {
                            messages: {
                                required: translations?.errors?.stateRequired || `${countryLabels.state} is required.`
                            }
                        })}
                    </FormGroup>
                    <FormGroup className={!cityId && validationFired && 'form-error'}>
                        <Label
                            htmlfor='city'
                            aria-label='city'
                            className='form-label'
                        >
                            {countryLabels.city}
                        </Label>
                        <span
                            aria-hidden='true'
                            className='required-indicator'
                        >
                            *
                        </span>
                        <DropDownAutoComplete
                            name='city'
                            id='city'
                            options={cities}
                            isDisabled={cities.length === 0}
                            className='form-control'
                            onChangedValue={handleChangeCities}
                            placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                            isFloatingDropdown={false}
                            onBlur={handleChangeCities}
                            defaultValue={cityId === '' ? undefined : cityId}
                            Value={cityId}
                        />
                        {validator.message('city', cityId, 'required', {
                            messages: {
                                required: translations?.errors?.cityRequired || `${countryLabels.city} is required.`
                            }
                        })}
                    </FormGroup>
                </>
            )}

            {(variableCountry === 'IT' || variableCountry === 'ES' || variableCountry === 'IE') && (
                <FormGroup className={!stateId && !states && validationFired && 'form-error'}>
                    <Label
                        htmlfor='state'
                        aria-label='state'
                        className='form-label'
                    >
                        {countryLabels.state}
                    </Label>
                    <DropDownAutoComplete
                        name='state'
                        id='state'
                        options={states}
                        className='form-control'
                        onChangedValue={handleStateDropDown}
                        placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                        isFloatingDropdown={false}
                        onBlur={handleStateDropDown}
                        defaultValue={stateId === '' ? undefined : stateId}
                        Value={stateId}
                    />
                </FormGroup>
            )}

            {variableCountry === 'MX' && (
                <>
                    <FormGroup className={!stateId && !states && validationFired && 'form-error'}>
                        <Label
                            htmlFor='state'
                            aria-label='state'
                            className='form-label'
                        >
                            {countryLabels.state}
                        </Label>
                        <span
                            aria-hidden='true'
                            className='required-indicator'
                        >
                            *
                        </span>
                        <DropDownAutoComplete
                            id='state'
                            name='state'
                            options={states}
                            className='form-control'
                            aria-required='true'
                            onChangedValue={handleStateDropDown}
                            isFloatingDropdown={false}
                            onBlur={handleStateDropDown}
                            defaultValue={stateId === '' ? undefined : stateId}
                            Value={stateId}
                            placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                        />
                        {validator.message('state', stateId, 'required', {
                            messages: {
                                required: translations?.errors?.stateRequired || `${countryLabels.state} is required.`
                            }
                        })}
                    </FormGroup>
                    <FormGroup>
                        <StandardInput
                            label={countryLabels.city}
                            hasError={!city && validationFired}
                            fieldName='city'
                            ariaLabelAndFieldId={'city'}
                            value={city}
                            maxLength={50}
                            onChange={handleInputChange}
                            validator={validator.message('city', city, 'required', {
                                messages: {
                                    required: translations?.errors?.cityRequired || `${countryLabels.city} is required.`
                                }
                            })}
                        />
                    </FormGroup>
                </>
            )}

            {(variableCountry === 'IE' || variableCountry === 'NZ') && (
                <StandardInput
                    hasError={zipValidator() && 'form-error'}
                    label={countryLabels.zip}
                    fieldName='zip'
                    ariaLabelAndFieldId={'zip'}
                    value={zip}
                    onChange={handleInputChange}
                    maxLength={10}
                    validator={zipValidator()}
                />
            )}

            {variableCountry === 'US' && (
                <>
                    <Row className='multiColWrapper'>
                        <Col sm='6'>
                            <FormGroup className={zipValidator() && validationFired && 'form-error'}>
                                <Label
                                    for='zipCode'
                                    className='form-label'
                                >
                                    {countryLabels.zip}
                                </Label>
                                <span
                                    aria-hidden='true'
                                    className='required-indicator'
                                >
                                    *
                                </span>
                                <Cleave
                                    options={{
                                        blocks: [5],
                                        numericOnly: true
                                    }}
                                    className='form-control'
                                    id='zipCode'
                                    name='zip'
                                    aria-required='true'
                                    onChange={handleInputChange}
                                    value={zip}
                                />
                                {zipValidator()}
                            </FormGroup>
                        </Col>
                        <Col
                            sm='6'
                            className={styles.mb24}
                        >
                            <FormGroup className={!stateId && validationFired && 'form-error'}>
                                <Label
                                    htmlfor='state'
                                    aria-label='state'
                                    className='form-label'
                                >
                                    {countryLabels.state}
                                </Label>
                                <span
                                    aria-hidden='true'
                                    className='required-indicator'
                                >
                                    *
                                </span>
                                <DropDownAutoComplete
                                    name='state'
                                    id='state'
                                    options={states}
                                    className='form-control'
                                    aria-required='true'
                                    onChangedValue={handleStateDropDown}
                                    isFloatingDropdown={false}
                                    onBlur={handleStateDropDown}
                                    defaultValue={stateId === '' ? undefined : stateId}
                                    Value={stateId}
                                    placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                                />
                                {validator.message('state', stateId, 'required', {
                                    messages: {
                                        required:
                                            translations?.errors?.stateRequired || `${countryLabels.state} is required.`
                                    }
                                })}
                            </FormGroup>
                        </Col>
                    </Row>
                    <StandardInput
                        label={countryLabels.city}
                        hasError={!city && validationFired}
                        fieldName='city'
                        ariaLabelAndFieldId={'city'}
                        value={city}
                        maxLength={50}
                        onChange={handleInputChange}
                        validator={validator.message('city', city, 'required', {
                            messages: {
                                required: translations?.errors?.cityRequired || `${countryLabels.city} is required.`
                            }
                        })}
                    />
                </>
            )}

            {(variableCountry === 'UK' || variableCountry === 'AUS') && (
                <>
                    <FormGroup className={variableCountry === 'AUS' && !stateId && validationFired && 'form-error'}>
                        <Label
                            htmlFor='state'
                            aria-label='state'
                            className='form-label'
                        >
                            {countryLabels.state}
                        </Label>
                        {variableCountry === 'AUS' && (
                            <span
                                aria-hidden='true'
                                className='required-indicator'
                            >
                                *
                            </span>
                        )}
                        <DropDownAutoComplete
                            id='state'
                            name='state'
                            options={states}
                            className='form-control'
                            aria-required='true'
                            onChangedValue={handleStateDropDown}
                            isFloatingDropdown={false}
                            onBlur={handleStateDropDown}
                            defaultValue={stateId === '' ? undefined : stateId}
                            Value={stateId}
                            placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                        />
                        {variableCountry === 'AUS'
                            && validator.message('state', stateId, variableCountry === 'UK' ? '' : 'required', {
                                messages: {
                                    required:
                                        translations?.errors?.stateRequired || `${countryLabels.state} is required.`
                                }
                            })}
                    </FormGroup>
                    {variableCountry === 'AUS' ? (
                        <FormGroup className={zipValidator() && 'form-error'}>
                            <Label
                                for='zipCode'
                                className='form-label'
                            >
                                {countryLabels.zip}
                            </Label>
                            <span
                                aria-hidden='true'
                                className='required-indicator'
                            >
                                *
                            </span>
                            <Cleave
                                options={{
                                    blocks: [4],
                                    numericOnly: true
                                }}
                                className='form-control'
                                id='zipCode'
                                name='zip'
                                aria-required='true'
                                onChange={(event) => handleInputChange(event)}
                                value={zip}
                            />
                            {zipValidator()}
                        </FormGroup>
                    ) : (
                        <StandardInput
                            hasError={zipValidator() && 'form-error'}
                            label={countryLabels.zip}
                            fieldName='zip'
                            ariaLabelAndFieldId={'zip'}
                            value={zip}
                            onChange={handleInputChange}
                            maxLength={8}
                            validator={zipValidator()}
                        />
                    )}
                </>
            )}

            {variableCountry === 'VN' && (
                <Row className={'multiColWrapper'}>
                    <Col
                        sm='6'
                        className={styles.mb24}
                    >
                        <FormGroup className={!stateId && validationFired && 'form-error'}>
                            <Label
                                htmlFor='state'
                                aria-label='state'
                                className='form-label'
                            >
                                {countryLabels.state}
                            </Label>
                            <span
                                aria-hidden='true'
                                className='required-indicator'
                            >
                                *
                            </span>
                            <DropDownAutoComplete
                                id='state'
                                name='state'
                                options={states}
                                className='form-control'
                                aria-required='true'
                                onChangedValue={handleStateDropDown}
                                isFloatingDropdown={false}
                                onBlur={handleStateDropDown}
                                defaultValue={stateId === '' ? undefined : stateId}
                                Value={stateId}
                                placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                            />
                            {validator.message('state', stateId, 'required', {
                                messages: {
                                    required:
                                        translations?.errors?.stateRequired || `${countryLabels.state} is required.`
                                }
                            })}
                        </FormGroup>
                    </Col>
                    <Col sm='6'>
                        <FormGroup>
                            <Label
                                for='zipCode'
                                className='form-label'
                            >
                                {countryLabels.zip}
                            </Label>
                            <Cleave
                                options={{
                                    blocks: [6],
                                    numericOnly: true
                                }}
                                className='form-control'
                                id='zipCode'
                                name='zip'
                                onChange={(event) => handleInputChange(event)}
                                value={zip}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {variableCountry === 'IN' && (
                <>
                    <FormGroup className={!stateId && validationFired && 'form-error'}>
                        <Label
                            htmlFor='state'
                            aria-label='state'
                            className='form-label'
                        >
                            {countryLabels.state}
                        </Label>
                        <span
                            aria-hidden='true'
                            className='required-indicator'
                        >
                            *
                        </span>
                        <DropDownAutoComplete
                            id='state'
                            name='state'
                            options={states}
                            className='form-control'
                            aria-required='true'
                            onChangedValue={handleStateDropDown}
                            isFloatingDropdown={false}
                            onBlur={handleStateDropDown}
                            defaultValue={stateId === '' ? undefined : stateId}
                            Value={stateId}
                            placeholder={translations.stateDropdownPlaceholder || 'Please select...'}
                        />
                        {validator.message('state', stateId, 'required', {
                            messages: {
                                required: translations?.errors?.stateRequired || `${countryLabels.state} is required.`
                            }
                        })}
                    </FormGroup>
                    <FormGroup className={zipValidator() && 'form-error'}>
                        <Label
                            for='zipCode'
                            className='form-label'
                        >
                            {countryLabels.zip}
                        </Label>
                        <span
                            aria-hidden='true'
                            className='required-indicator'
                        >
                            *
                        </span>
                        <Cleave
                            options={{
                                blocks: [6],
                                numericOnly: true
                            }}
                            className='form-control'
                            id='zipCode'
                            name='zip'
                            aria-required='true'
                            onChange={(event) => handleInputChange(event)}
                            value={zip}
                        />
                        {zipValidator()}
                    </FormGroup>
                </>
            )}

            {allowVariableCountry && (
                <AppleIntlReplacementModal
                    nextCountry={tempNextCountry}
                    isOpen={isChangeCountryModalOpen}
                    onClose={() => setIsChangeCountryModalOpen(false)}
                    onConfirm={handleChangeCountryConfirm}
                />
            )}
        </div>
    );
};

export const StaticAddress = ({
    addressData,
    showCountry = false,
    showName = false,
    showContactDetails = false,
    countryCode,
    states,
    clientId,
    phone = '',
    email = '',
    customClass = ''
}) => {
    let country = addressData?.countryCode || addressData?.country;
    let transCountry = useTranslation().country;

    if (!country) {
        country = transCountry;
    }

    let { firstName, lastName, address1, address2, zip, city, state, plus4 } = addressData;

    let nameString =
        showName && (firstName || address2) && `${!!firstName && firstName + ' '}${!!lastName && lastName}`;
    const countryName = countryNameFromCode(country);
    const [stateFullName, setStateFullName] = useState('');
    const [stateAbbr, setStateAbbr] = useState('');
    const international = useTranslation('components/Vendor/Apple/IntakeLoss/CountryAddressForms');
    const { language, translations } = international;

    if (typeof nameString == 'undefined' && addressData.length > 2) {
        nameString = addressData[0];
        address1 = addressData[1];
        address2 = addressData[2];
    }

    useEffect(() => {
        if (!states || !states.length) {
            fetchStates();
        } else {
            const selectedState =
                (states
                    && states.filter(
                        (st, ind) =>
                            st.abbreviation === state || st.display === state || st.data === addressData?.stateId
                    ))
                || [];
            if (selectedState.length) {
                setStateAbbr(selectedState[0]?.abbreviation);
                setStateFullName(selectedState[0]?.display);
            }
        }
    }, [clientId]);

    useEffect(() => {
        if (!states || !states.length) {
            fetchStates();
        }
    }, []);

    const fetchStates = async () => {
        await fetch('api/party/states/' + country + '/' + clientId)
            .then((res) => res.json())
            .then((data) => {
                let results = data.map((s) => {
                    return { data: s.stateId, display: s.name, abbreviation: s.abbreviation };
                });
                const selectedState =
                    (results
                        && results.filter(
                            (st, ind) =>
                                st.abbreviation === state || st.display === state || st.data === addressData?.stateId
                        ))
                    || [];
                if (selectedState.length) {
                    setStateFullName(selectedState[0]?.display);
                    setStateAbbr(selectedState[0]?.abbreviation);
                }
            });
    };

    useEffect(() => {
        if (country?.toUpperCase() === 'JP') {
            const selectedState =
                (states
                    && states.filter(
                        (st, ind) =>
                            st.abbreviation === state || st.display === state || st.data === addressData?.stateId
                    ))
                || [];
            setStateFullName(
                (translations.states && translations.states[selectedState[0]?.display?.toLowerCase()])
                    || selectedState[0]?.display
            );
        }
    }, [translations.states, stateFullName]);

    const showAddressText = () => {
        switch (country?.toUpperCase()) {
            case 'UK':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        {city && <li>{city}</li>}
                        {stateAbbr && <li>{stateAbbr}</li>}
                        {zip && <li>{zip}</li>}
                    </>
                );
            case 'AT':
            case 'DK':
            case 'FI':
            case 'FR':
            case 'DE':
            case 'NL':
            case 'NO':
            case 'SE':
            case 'CH':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        <li>
                            {zip && zip + ' '}
                            {city && city}
                        </li>
                    </>
                );
            case 'IT':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        <li>
                            {zip && zip + ' '}
                            {city && city + ' '}
                            {stateAbbr && stateAbbr}
                        </li>
                    </>
                );
            case 'ES':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        <li>
                            {zip && zip + ' '}
                            {city && city}
                        </li>
                        {stateAbbr && <li>{stateAbbr}</li>}
                    </>
                );
            case 'VN':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && (
                            <li>
                                {address2 + ', '}
                                {city && city}
                            </li>
                        )}
                        {stateFullName && <li>{stateFullName}</li>}
                        {zip && <li>{zip}</li>}
                    </>
                );
            case 'CL':
                return (
                    <>
                        <li>
                            {address1 && address1}
                            {address2 && `, ${address2}`}
                        </li>
                        <li>{zip && zip + ' '}</li>
                        {city && <li>{city}</li>}
                        {stateFullName && <li>{stateFullName}</li>}
                    </>
                );
            case 'IE':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        {city && <li>{city}</li>}
                        {stateFullName && <li>{stateFullName}</li>}
                        {zip && <li>{zip}</li>}
                    </>
                );
            case 'NZ':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        <li>
                            {city && city + ' '}
                            {zip && zip}
                        </li>
                    </>
                );
            case 'JP':
                return (
                    <li>
                        {zip && `${zip}${plus4 ? '-' + plus4 : ''}, `}
                        {stateFullName && stateFullName + ' '}
                        {city && city + ' '}
                        {address1 && address1 + ' '}
                        {address2 && address2}
                    </li>
                );
            case 'MX':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        <li>
                            {zip && zip + ', '}
                            {city && city + ', '}
                            {stateAbbr && stateAbbr}
                        </li>
                    </>
                );
            case 'IN':
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        <li>
                            {city && city + ' '}
                            {zip && zip + ', '}
                            {stateAbbr && stateAbbr}
                        </li>
                    </>
                );
            case 'US':
            case 'AUS':
            default:
                return (
                    <>
                        {address1 && <li>{address1}</li>}
                        {address2 && <li>{address2}</li>}
                        <li>
                            {city && city + ', '}
                            {stateAbbr && stateAbbr + ' '}
                            {zip && zip}
                        </li>
                    </>
                );
        }
    };

    // RETURN NOTHING IF ALL VALUES ARE FALSY
    if (Object.values(addressData ? addressData : '').every((v) => !v)) return null;

    return (
        <ul
            id='billingAddressStatic'
            className={`noBullets ${customClass}`}
            role='none'
        >
            {showName && <li>{nameString}</li>}
            {showAddressText()}
            <li>{countryName}</li>
            {showContactDetails && (
                <>
                    {phone && <li>{phone}</li>}
                    {email && <li>{email}</li>}
                </>
            )}
        </ul>
    );
};
